import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { DataTreeItem, GatewaySensorType } from '../../services/api';
import { getSensorsById } from '../../state/selectors';
import { useAppSelector } from '../../state/store';
import { ListOptions } from '../../Views/OfflineView';
import OfflineLocSummary from './OfflineLocSummary';
import OfflineSensorSummary from './OfflineSensorSummary';
import OfflineGatewaySummary from './OfflineGatewaySummary';

// Helper Component: Renders the list of sub-locations when a parent location is only partially offline.
function PartialOfflineLocation({
  offlineSensors,
  locSensors,
  excludeLocations, // fully offline locations
  listType,
  handleTagging,
}: {
  offlineSensors: DataTreeItem[];
  locSensors: DataTreeItem[];
  excludeLocations: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}) {
  const sensorsById = useAppSelector(getSensorsById);

  // Group sensors by location once, instead of re-filtering offlineSensors/locSensors
  // for every sub-location on every render
  const { validSubLocations, offlineSensorsByLoc, locSensorsByLoc } = useMemo(() => {
    const offlineMap = new Map<string, DataTreeItem[]>();
    offlineSensors.forEach((item) => {
      if (!item.location || excludeLocations.has(item.location)) return;
      const existing = offlineMap.get(item.location);
      if (existing) {
        existing.push(item);
      } else {
        offlineMap.set(item.location, [item]);
      }
    });

    const locMap = new Map<string, DataTreeItem[]>();
    locSensors.forEach((item) => {
      if (!item.location) return;
      const existing = locMap.get(item.location);
      if (existing) {
        existing.push(item);
      } else {
        locMap.set(item.location, [item]);
      }
    });

    return {
      validSubLocations: Array.from(offlineMap.keys()),
      offlineSensorsByLoc: offlineMap,
      locSensorsByLoc: locMap,
    };
  }, [offlineSensors, locSensors, excludeLocations]);

  if (validSubLocations.length === 0) return null;

  return (
    <Box>
      {validSubLocations.map((subLocation) => {
        const subLocOfflineSensors = offlineSensorsByLoc.get(subLocation) ?? [];
        const allSubLocSensors = locSensorsByLoc.get(subLocation) ?? [];

        if (allSubLocSensors.length === 0) return null;

        // Identify offline gateways
        const offlineGatewaySet = new Set(
          subLocOfflineSensors
            .filter((sensor) => sensorsById.get(sensor.id)?.type === GatewaySensorType.LfWiFiSensor)
            .map((s) => s.id)
        );
        const offlineGateways = Array.from(offlineGatewaySet);

        // Filter faulty sensors (exclude those whose gateway is already offline, to avoid
        // double reporting). A sensor with no gateway has nothing to double-report against,
        // so it stays in the list rather than being dropped.
        const filteredFaultySensors = subLocOfflineSensors.filter((sensor) => {
          const gateway = sensorsById.get(sensor.id)?.gateway;
          return !gateway || !offlineGatewaySet.has(gateway);
        });

        const isLocFullyOffline = allSubLocSensors.length === subLocOfflineSensors.length;

        return (
          <OfflineLocSummary
            key={subLocation}
            locId={subLocation}
            listType={listType}
            locSensors={allSubLocSensors}
            offlineSensors={subLocOfflineSensors}
            handleTagging={handleTagging}
          >
            <Box>
              <OfflineGatewaySummary
                allSensors={allSubLocSensors}
                isLocOffline={isLocFullyOffline}
                listType={listType}
                handleTagging={handleTagging}
                offlineGateways={offlineGateways}
              />
              <OfflineSensorSummary
                faultySensors={filteredFaultySensors}
                listType={listType}
                handleTagging={handleTagging}
              />
            </Box>
          </OfflineLocSummary>
        );
      })}
    </Box>
  );
}
export default PartialOfflineLocation;
